/**
 * Video behaviour: the factory clip in "Who are we" and the customers'
 * clips in "Proof in every cup".
 *
 * Ported from components/sections/FactoryVideo.tsx and
 * components/sections/Testimonials.tsx. Plain ES module, no framework;
 * bound to the markup through data attributes and re-bound when the theme
 * editor reloads a section.
 */

/* ------------------------------------------------------------------ */
/* Factory video: autoplay, loop, resume, photograph only when it fails  */
/* ------------------------------------------------------------------ */

/**
 * Silent, looped and started as soon as the block scrolls into view;
 * nothing on the page can pause it. The clip is not downloaded until the
 * block is within a screen of the viewport (the markup carries the source
 * URLs as data attributes and preload="none"), so pages where it sits far
 * down no longer spend megabytes on it before their first paint. Browsers do stop autoplay
 * video when it leaves the screen or the tab goes to the background, so the
 * element is nudged back into play whenever it comes back into view, the
 * tab is shown again, or the browser pauses it for any reason.
 *
 * If the video cannot load at all the factory photograph (data-fallback)
 * takes its place, so the block never shows an empty box.
 */
function bindFactoryVideo(video) {
  if (video.dataset.videoBound === '1') return;
  video.dataset.videoBound = '1';

  let failed = false;
  let loaded = false;
  let near = false;

  // Visitors who asked their browser to save data keep the poster only.
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  const play = () => {
    if (failed || !loaded || !near || !video.paused) return;
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const fail = () => {
    if (failed) return;
    failed = true;
    io.disconnect();
    document.removeEventListener('visibilitychange', onVisible);
    video.removeEventListener('pause', onPause);
    const img = document.createElement('img');
    img.src = video.dataset.fallback || '';
    img.className = 'custom-it-img';
    img.alt = '';
    img.width = 1000;
    img.height = 666;
    img.loading = 'lazy';
    img.decoding = 'async';
    video.replaceWith(img);
  };

  // One <source> failing is not the video failing: the browser moves on to
  // the next source. Source errors do not bubble, so they are caught in the
  // capture phase on the element. Only when it has run out of sources is the
  // photograph shown.
  const check = () => {
    window.setTimeout(() => {
      if (video.networkState === video.NETWORK_NO_SOURCE) fail();
    }, 150);
  };

  // The clip is only fetched when the block is about to be seen: the
  // sources go in (webm first, the smaller file), then load().
  const load = () => {
    if (loaded || saveData) return;
    loaded = true;
    const srcs = [
      [video.dataset.srcWebm, 'video/webm'],
      [video.dataset.srcMp4, 'video/mp4'],
    ];
    for (const [src, type] of srcs) {
      if (!src) continue;
      const s = document.createElement('source');
      s.src = src;
      s.type = type;
      video.appendChild(s);
    }
    if (!video.querySelector('source')) {
      fail();
      return;
    }
    video.addEventListener('error', check, true);
    video.preload = 'auto';
    video.load();
  };

  // Fetch when the block is within 200 px of the screen (the poster, the
  // clip's own first frame, covers the moment it takes to start); play while
  // on screen, and keep starting every time it reappears. A screen ahead was
  // too early: on a desktop landing page the block sits just under the fold,
  // so the whole clip downloaded with the page.
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        near = e.isIntersecting;
        if (near) {
          load();
          play();
        }
      }
    },
    { rootMargin: '200px 0px', threshold: 0 },
  );
  io.observe(video);
  video.addEventListener('canplay', play);

  // The browser pauses background tabs; resume when the tab is back.
  const onVisible = () => {
    if (document.visibilityState === 'visible') play();
  };
  document.addEventListener('visibilitychange', onVisible);

  // Anything else that pauses it (power saving, a data-saver prompt that
  // the viewer dismissed) gets resumed on the next frame.
  const onPause = () => {
    if (!video.ended && document.visibilityState === 'visible') requestAnimationFrame(play);
  };
  video.addEventListener('pause', onPause);
}

/* ------------------------------------------------------------------ */
/* Testimonial clips: play on scroll, one sound at a time, quote jumps   */
/* ------------------------------------------------------------------ */

const fmt = (s) => {
  const n = Math.max(0, Math.floor(Number.isFinite(s) ? s : 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

function bindTestimonials(section) {
  if (section.dataset.videoBound === '1') return;
  section.dataset.videoBound = '1';

  const videos = Array.from(section.querySelectorAll('video.proof-video[data-id]'));
  if (videos.length === 0) return;
  const lines = Array.from(section.querySelectorAll('[data-proof-line]'));
  const sounds = Array.from(section.querySelectorAll('[data-proof-sound]'));

  // Posters and metadata wait until a film is near the screen (data-poster,
  // preload="none" in the markup), so they never compete with the page's
  // first paint. Without IntersectionObserver they load at once.
  const wake = (v) => {
    if (v.dataset.poster) {
      v.poster = v.dataset.poster;
      v.removeAttribute('data-poster');
    }
    if (v.preload === 'none') v.preload = 'metadata';
  };
  if ('IntersectionObserver' in window) {
    const near = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          wake(e.target);
          near.unobserve(e.target);
        }
      },
      { rootMargin: '600px 100%' },
    );
    videos.forEach((v) => near.observe(v));
  } else {
    videos.forEach(wake);
  }

  let loud = null;
  const paused = new Set();
  const time = new Map();
  // Tracked from the element's own play/pause events, so a clip the observer
  // paused off-screen is "not playing" even though its time is still inside a
  // quote's span, and the quote stops glowing.
  const playing = new Set();

  const video = (id) => videos.find((v) => v.dataset.id === id) || null;
  const frameOf = (v) => v.closest('[data-proof-frame]') || null;

  const safePlay = (v) => {
    if (!v) return;
    wake(v);
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  function renderLines() {
    for (const li of lines) {
      const id = li.dataset.clip || '';
      const cur = time.get(id);
      const at = Number(li.dataset.at) || 0;
      const until = Number(li.dataset.until) || 0;
      const live = !!cur && playing.has(id) && cur.t >= at && cur.t < until;
      li.classList.toggle('is-live', live);
    }
  }

  function renderClip(v) {
    const id = v.dataset.id || '';
    const cur = time.get(id);
    const frame = frameOf(v);
    if (frame) {
      const bar = frame.querySelector('[data-proof-progress]');
      const pct = cur && cur.d > 0 ? (cur.t / cur.d) * 100 : 0;
      if (bar) bar.style.width = `${pct}%`;
    }
    const figure = v.closest('.proof-clip');
    const t = figure && figure.querySelector('[data-proof-time]');
    if (t) t.textContent = `${fmt(cur ? cur.t : 0)} / ${fmt(cur ? cur.d : 0)}`;
  }

  function renderSound() {
    for (const btn of sounds) {
      const id = btn.dataset.clip || '';
      const name = btn.dataset.name || '';
      const on = loud === id;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? `Turn sound off, ${name}` : `Turn sound on, ${name}`);
      const onIcon = btn.querySelector('[data-sound-on]');
      const offIcon = btn.querySelector('[data-sound-off]');
      if (onIcon) onIcon.style.display = on ? '' : 'none';
      if (offIcon) offIcon.style.display = on ? 'none' : '';
      const label = btn.querySelector('[data-proof-sound-label]');
      if (label) label.textContent = on ? 'Sound on' : 'Sound';
    }
  }

  function setSound(id) {
    loud = id;
    for (const v of videos) v.muted = v.dataset.id !== id;
    renderSound();
  }

  function toggleSound(id) {
    const next = loud === id ? null : id;
    setSound(next);
    if (next) {
      paused.delete(id);
      safePlay(video(id));
    }
  }

  function togglePlay(v) {
    if (!v) return;
    const id = v.dataset.id || '';
    if (v.paused) {
      paused.delete(id);
      safePlay(v);
    } else {
      paused.add(id);
      v.pause();
    }
  }

  /** The quote's own moment: seek there, sound on, and bring the clip into view. */
  function jumpTo(id, at) {
    const v = video(id);
    if (!v) return;
    v.currentTime = at;
    setSound(id);
    paused.delete(id);
    safePlay(v);
    const frame = frameOf(v);
    if (frame && typeof frame.scrollIntoView === 'function') {
      frame.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  for (const v of videos) {
    const id = v.dataset.id || '';
    v.muted = true;
    v.addEventListener('click', () => togglePlay(v));
    v.addEventListener('play', () => {
      playing.add(id);
      renderLines();
    });
    v.addEventListener('pause', () => {
      playing.delete(id);
      renderLines();
    });
    const tick = () => {
      time.set(id, { t: v.currentTime, d: v.duration || 0 });
      renderClip(v);
      renderLines();
    };
    // Only timeupdate, as Testimonials.tsx (onTimeUpdate): the time reads
    // 0:00 / 0:00 until the clip starts playing.
    v.addEventListener('timeupdate', tick);
    renderClip(v);
  }

  for (const li of lines) {
    const btn = li.querySelector('[data-proof-jump]');
    if (!btn) continue;
    btn.addEventListener('click', () => jumpTo(li.dataset.clip || '', Number(li.dataset.at) || 0));
  }

  for (const btn of sounds) {
    btn.addEventListener('click', () => toggleSound(btn.dataset.clip || ''));
  }

  renderSound();
  renderLines();

  // Play what is on screen, stop what is not. Under a reduced-motion setting
  // nothing starts by itself.
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const v = e.target;
        if (e.intersectionRatio >= 0.4) {
          if (!paused.has(v.dataset.id || '')) safePlay(v);
        } else {
          v.pause();
        }
      }
    },
    { threshold: [0, 0.4, 1] },
  );
  videos.forEach((v) => io.observe(v));
}

/* ------------------------------------------------------------------ */

function init(root = document) {
  root.querySelectorAll('video[data-factory-video]').forEach(bindFactoryVideo);
  root.querySelectorAll('[data-testimonials]').forEach(bindTestimonials);
  if (root !== document && root.matches) {
    if (root.matches('video[data-factory-video]')) bindFactoryVideo(root);
    if (root.matches('[data-testimonials]')) bindTestimonials(root);
  }
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
